import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  "[tabindex]:not([tabindex='-1'])",
].join(',');

export function useDialogFocus(active: boolean, onEscape?: (() => void) | null) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const escapeHandlerRef = useRef(onEscape);

  useEffect(() => {
    escapeHandlerRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (!active || !dialogRef.current) return undefined;

    const dialog = dialogRef.current;
    const previousFocus = document.activeElement as HTMLElement | null;
    const parent = dialog.parentElement;
    const isBackdrop = parent?.classList.contains('purchase-gate-backdrop');
    const siblings = isBackdrop
      ? [...(parent!.parentElement?.children || [])].filter((element) => element !== parent)
      : [...(parent?.children || [])].filter((element) => element !== dialog);
    const siblingState = siblings.map((element) => ({
      element: element as HTMLElement,
      inert: (element as HTMLElement & { inert?: boolean }).inert,
      ariaHidden: element.getAttribute('aria-hidden'),
    }));

    siblings.forEach((element) => {
      (element as HTMLElement & { inert?: boolean }).inert = true;
      element.setAttribute('aria-hidden', 'true');
    });

    const focusFirstControl = window.requestAnimationFrame(() => {
      const firstControl = dialog.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstControl || dialog).focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && escapeHandlerRef.current) {
        event.preventDefault();
        escapeHandlerRef.current();
        return;
      }

      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)]
        .filter((element) => element.getClientRects().length > 0);
      if (!controls.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFirstControl);
      document.removeEventListener('keydown', handleKeyDown);
      siblingState.forEach(({ element, inert, ariaHidden }) => {
        (element as HTMLElement & { inert?: boolean }).inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [active]);

  return dialogRef;
}
